/**
 * contexts/AuthContext.tsx
 * ------------------------------------------------------------------
 * Authentication for the single-operator phase, now with three entry
 * methods:
 *   - Google sign-in   (signInWithGoogle) — the operator email is a
 *                       Google Workspace account, so this is the
 *                       natural primary method. First Google sign-in
 *                       also doubles as sign-up.
 *   - Email sign-in     (signIn)
 *   - Email sign-up     (signUp) — create an account without the
 *                       Firebase console.
 *
 * ── Authentication vs authorisation ──────────────────────────────
 * Any of the above AUTHENTICATES a user with Firebase. Whether that
 * user is AUTHORISED to use the app is a separate check, unchanged:
 * authorised iff the email matches the configured operator email OR
 * an active record exists in the `users` collection. A stranger who
 * signs up with Google is authenticated but lands on the "access not
 * enabled" screen — so open sign-up is safe.
 *
 * Adding a teammate later is therefore a pure data change: create a
 * `users/{uid}` doc with role + active:true. No code change.
 * ------------------------------------------------------------------
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { COLLECTIONS, type AppUser, type UserRole } from '@/types';

const OPERATOR_EMAIL = (
  (import.meta.env.VITE_OPERATOR_EMAIL as string) || ''
).toLowerCase();

interface AuthState {
  user: User | null;
  role: UserRole | null;
  authorised: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

async function resolveRole(fbUser: User): Promise<UserRole | null> {
  // 1) Operator bootstrap: configured email is always the system admin.
  const email = (fbUser.email || '').toLowerCase();
  if (OPERATOR_EMAIL && email === OPERATOR_EMAIL) return 'admin';

  // 2) Otherwise consult the allowlist. It is keyed by lowercased email
  //    so the admin can provision a teammate before they ever sign in.
  try {
    if (email) {
      const byEmail = await getDoc(doc(db, COLLECTIONS.users, email));
      if (byEmail.exists()) {
        const data = byEmail.data() as AppUser;
        return data.active ? data.role : null;
      }
    }
    // Legacy fallback: older records keyed by uid.
    const byUid = await getDoc(doc(db, COLLECTIONS.users, fbUser.uid));
    if (byUid.exists()) {
      const data = byUid.data() as AppUser;
      return data.active ? data.role : null;
    }
  } catch (err) {
    console.error('[auth] failed to resolve user role', err);
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setUser(fbUser);
      if (!fbUser) {
        setRole(null);
        setLoading(false);
        return;
      }
      setRole(await resolveRole(fbUser));
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      role,
      authorised: !!user && role !== null,
      loading,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      },
      signUp: async (email, password) => {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      },
      signInWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        await signInWithPopup(auth, provider);
      },
      signOut: async () => {
        await fbSignOut(auth);
      },
    }),
    [user, role, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
